import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import BorderlessButton from '../common/buttons/BorderlessButton';
import PanelLeftCloseIcon from '../common/icons/files/PanelLeftCloseIcon';
import PanelLeftOpenIcon from '../common/icons/files/PanelLeftOpenIcon';
import UnassignedScheduleCategory from './UnassignedScheduleCategory';
import { moveShift } from './state/schedule.actions';

export default function UnassignedScheduleSidebar({
  open,
  shifts,
  onSidebarClose,
  onSidebarOpen,
  onShiftSelect,
}) {
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.schedule.categories);

  const relevantCategories = useMemo(() => (
    categories.concat([{
      id: null,
      name: 'Other',
      color: '#FFFFFF',
    }])
  ), [categories]);

  const onMoveRow = useCallback(async ({
    newCategoryId,
    oldCategoryId,
    oldId,
  }) => {
    if (oldCategoryId === newCategoryId) return;
    await dispatch(moveShift(oldId, newCategoryId));
  }, []);

  const shiftCategoryMap = useMemo(() => {
    const shiftSet = new Set();
    return Object.values(shifts).flat().reduce((acc, shift) => {
      if (shift.isEvent || shift.users?.length || shiftSet.has(shift.id)) return acc;

      const relevantCategoryId = shift.shiftCategoryId || null;

      if (!acc[relevantCategoryId]) {
        acc[relevantCategoryId] = [];
      }

      acc[relevantCategoryId].push({
        ...shift,
        shiftCategoryId: relevantCategoryId,
      });
      shiftSet.add(shift.id);
      return acc;
    }, {});
  }, [shifts]);

  const unassignedCount = useMemo(() => (
    Object.values(shiftCategoryMap).reduce((sum, categoryShifts) => sum + categoryShifts.length, 0)
  ), [shiftCategoryMap]);

  return (
    <div className={`schedule-sidebar-container ${open ? 'schedule-sidebar-open' : 'schedule-sidebar-closed'}`}>
      <div className="schedule-sidebar-title">
        <div className="schedule-sidebar-title-label">
          <div className={open ? 'schedule-sidebar-open-title' : 'schedule-sidebar-closed-title'}>Dispatch Queue</div>
          {unassignedCount > 0 && (
            <div className="schedule-sidebar-count">{unassignedCount}</div>
          )}
        </div>
        <BorderlessButton
          iconNode={open ? <PanelLeftCloseIcon /> : <PanelLeftOpenIcon />}
          onClick={open ? onSidebarClose : onSidebarOpen}
          style={{
            background: 'transparent',
            width: 20,
            padding: 0,
          }}
        />
      </div>
      {
        open && relevantCategories.map((category) => (
          <UnassignedScheduleCategory
            key={category.id}
            category={category}
            shifts={shiftCategoryMap[category.id]}
            onMoveRow={onMoveRow}
            onShiftSelect={onShiftSelect}
          />
        ))
      }
    </div>
  );
}

UnassignedScheduleSidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  shifts: PropTypes.shape({}).isRequired,
  onSidebarClose: PropTypes.func.isRequired,
  onSidebarOpen: PropTypes.func.isRequired,
  onShiftSelect: PropTypes.func.isRequired,
};
